import {
  EyeOutlined,
  CheckCircleFilled,
  AudioMutedOutlined,
  BellOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { orderBy, cloneDeep } from "lodash";
import moment from "moment-timezone";
import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Button, Table, Collapse, Tooltip, Typography, Input } from "antd";

import { DocumentStatus, DOCUMENT_STATUS_METADATA } from "../../../constants/documents";
import { GET_DOCUMENT_LIST } from "./gql";
import { ShowError } from "../../../components/error";
import { formatCreatedAt, renderExpiryCell, renderStatusCell } from "./documentFieldUtils";

const { Text } = Typography;

// We try to get the requirement name from the first requirement
// listed in the fulfilledRequirements field.
// This is related to ticket CH-2665, where documents are being
// restructured to only meet a single requirement instead of
// possibly meeting multiple ones.
const getRequirementName = (document) => {
  let requirementName = "Miscellaneous";

  if (document.fulfilledRequirement) {
    requirementName = document.fulfilledRequirement.name || "...";
  }

  return requirementName;
};

function DocumentTable({ setSelectedDoc, agentId, agentTMZ }) {
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [documentList, setDocumentList] = useState<any>();
  const [searchText, setSearchText] = useState("");

  const onDocumentQueryCompleted = (data) => {
    const documents = cloneDeep(data.documentList).map((document) => {
      document.requirementName = getRequirementName(document);
      return document;
    });

    const orderedDocumentList = orderDocuments(documents, "status", "asc");
    setDocumentList(orderedDocumentList);
  };

  const { loading, error } = useQuery(GET_DOCUMENT_LIST, {
    variables: { hcpId: agentId },
    fetchPolicy: "cache-and-network",
    onCompleted: onDocumentQueryCompleted,
  });

  const onDocumentClick = (document) => () => setSelectedDoc(document);

  const renderPreviewButton = (_, document) => {
    const isPending = document.status === DocumentStatus.PENDING;

    return (
      <Button
        size="small"
        onClick={onDocumentClick(document)}
        className={isPending ? "volcano-icon-button" : ""}
      >
        <EyeOutlined />
      </Button>
    );
  };

  const renderExpiry = (document) => {
    const { requirement: { expiryType } = { expiryType: "" } } = document;
    return renderExpiryCell({
      expiryType,
      expiryDate: document?.expiry,
      wrapWithPopover: true,
    });
  };

  const renderRejectionNotificationSent = (_, document) => {
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { notification, rejectionNotifiedAt } = document;
    return (
      document?.status === DocumentStatus.REJECTED && (
        <div>
          {rejectionNotifiedAt ? (
            <Tooltip
              title={
                rejectionNotifiedAt
                  ? moment(rejectionNotifiedAt)
                      .tz("America/Los_Angeles")
                      .format("MMM DD, YYYY, hh:mm A z")
                  : ""
              }
            >
              <CheckCircleFilled style={{ color: "green" }} />
            </Tooltip>
          ) : (
            <AudioMutedOutlined />
          )}
        </div>
      )
    );
  };

  const renderReasonForRejection = (_, document) => {
    const reason = document?.notification?.reason;
    const note = document?.notification?.note;

    return (
      <div>
        <h4>
          {reason}
          {note && (
            <Tooltip title={note}>
              <InfoCircleOutlined
                style={{ marginLeft: 6, color: "#999", fontSize: 14, cursor: "pointer" }}
              />
            </Tooltip>
          )}
        </h4>
      </div>
    );
  };

  const renderRequirementName = (requirementName) => (
    <div style={{ wordWrap: "break-word", wordBreak: "break-word" }}>{requirementName}</div>
  );

  const renderCreatedAt = (_, document) => {
    const formattedDate = formatCreatedAt(document.createdAt, agentTMZ);
    return formattedDate ? <Text>{formattedDate}</Text> : null;
  };

  const orderDocuments = (documents, field, order) => {
    if (field === "status") {
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line no-param-reassign
      field = (document) => DOCUMENT_STATUS_METADATA[document.status].order;
    }
    return orderBy(documents, [field], [order]);
  };

  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOnChange = (pagination, filter, tableSort) => {
    const { field } = tableSort;
    const order = tableSort.order === "ascend" ? "asc" : "desc";

    const orderedDocumentList = orderDocuments(documentList, field, order);
    setDocumentList(orderedDocumentList);
  };

  const filteredDocumentList = searchText
    ? documentList?.filter((document) =>
        document.requirementName.toLowerCase().includes(searchText.toLowerCase())
      )
    : documentList;

  return (
    <>
      <ShowError message="Unable to load the documents" error={error} />
      <Collapse>
        <Collapse.Panel id="documentsTable" header="Documents Table" key="Documents Table">
          <Input
            allowClear
            placeholder="Search by document name..."
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            style={{ marginBottom: 16, maxWidth: 350 }}
          />
          <Table
            columns={[
              {
                title: "Name",
                dataIndex: "requirementName",
                render: renderRequirementName,
                sorter: true,
              },
              {
                title: "Created",
                dataIndex: "createdAt",
                render: renderCreatedAt,
                sorter: true,
                width: 150,
              },
              {
                title: "Rejection Reason",
                dataIndex: "rejectionReason",
                render: renderReasonForRejection,
                sorter: true,
              },
              {
                title: <BellOutlined />,
                dataIndex: "notificationSent",
                render: renderRejectionNotificationSent,
                sorter: false,
              },
              {
                title: "Status",
                dataIndex: "status",
                render: renderStatusCell,
                sorter: true,
              },
              {
                title: "Expiry",
                render: renderExpiry,
                sorter: true,
              },
              {
                title: "",
                dataIndex: "_id",
                render: renderPreviewButton,
              },
            ]}
            dataSource={filteredDocumentList}
            loading={loading}
            pagination={{ hideOnSinglePage: true, showSizeChanger: false }}
            rowKey="_id"
            size="small"
            onChange={handleOnChange}
          />
        </Collapse.Panel>
      </Collapse>
    </>
  );
}

export { DocumentTable };
